export * from "./computation/index";
export * from "./formatting/index";
export * from "./utils/index";
export * from "./queue";
export * from "./promotions";
import { DateRangeType } from "@lemmonhq/types";
export { daysOfWeek, debounce } from "./constants";

export const getTableJoinURL = (
  HOST: string,
  shopSlug: string,
  table: { id: number; guid: string },
) => {
  return `${HOST}/api/join/${shopSlug}/${table.guid}`;
};

// Generic filter serialization/deserialization helpers for URL query params
export interface DateRangeFilter {
  type: string;
  startDate?: string;
  endDate?: string;
}

export interface GenericFilters {
  dateRange?: DateRangeFilter;
  [key: string]: any;
}

/**
 * Serializes filters to URL query parameters
 * For date ranges with type "custom", includes startDate and endDate
 * For other date range types, only includes the type
 * @param filters - Filters to serialize
 * @param options - Configuration options
 *   - defaultDateRangeType: Default date range type to exclude from URL (defaults to "this_week")
 */
export const serializeFiltersToQuery = (
  filters: GenericFilters,
  options?: {
    defaultDateRangeType?: string;
  },
): Record<string, string> => {
  const query: Record<string, string> = {};
  const defaultDateRangeType = options?.defaultDateRangeType || "this_week";

  Object.keys(filters).forEach((key) => {
    const value = filters[key];

    if (value === undefined) return;

    // Special handling for dateRange
    if (key === "dateRange" && typeof value === "object") {
      const { type, startDate, endDate } = value;

      if (type === "custom" && startDate && endDate) {
        query.dateRangeType = type;
        query.startDate = startDate;
        query.endDate = endDate;
      } else if (type && type !== defaultDateRangeType) {
        // Only include non-default date range types
        query.dateRange = type;
      }
    }
    // Handle primitives: string, number, boolean
    else if (typeof value === "string" || typeof value === "number") {
      query[key] = value.toString();
    } else if (typeof value === "boolean") {
      // Only include boolean if it's true
      if (value) {
        query[key] = "1";
      }
    }
  });

  return query;
};

/**
 * Deserializes URL query parameters back to filters
 * Reconstructs date ranges from either type or custom startDate/endDate
 * @param query - URL query parameters
 * @param getDateRangeFn - Function to get date range from type
 * @param options - Configuration options
 *   - booleanFields: Array of field names that should be treated as booleans
 *   - numberFields: Array of field names that should be converted to numbers
 *   - defaultDateRangeType: Default date range type (defaults to "this_week")
 */
export const deserializeFiltersFromQuery = (
  query: Record<string, any>,
  getDateRangeFn: (type: DateRangeType) => {
    startDate: string;
    endDate: string;
  },
  options?: {
    booleanFields?: string[];
    numberFields?: string[];
    defaultDateRangeType?: string;
  },
): GenericFilters => {
  const filters: GenericFilters = {};
  const booleanFields = options?.booleanFields || [];
  const numberFields = options?.numberFields || [];
  const defaultDateRangeType = (options?.defaultDateRangeType ||
    "this_week") as DateRangeType;

  // Handle dateRange
  if (query.dateRangeType === "custom" && query.startDate && query.endDate) {
    // Custom date range with explicit dates
    filters.dateRange = {
      type: "custom",
      startDate: query.startDate as string,
      endDate: query.endDate as string,
    };
  } else if (query.dateRange) {
    // Predefined date range type
    const dateRangeType = query.dateRange as DateRangeType;
    const dateRange = getDateRangeFn(dateRangeType);
    filters.dateRange = {
      type: dateRangeType,
      ...dateRange,
    };
  } else {
    // Default date range
    const dateRange = getDateRangeFn(defaultDateRangeType);
    filters.dateRange = {
      type: defaultDateRangeType,
      ...dateRange,
    };
  }

  // Handle number fields
  numberFields.forEach((field) => {
    if (query[field] !== undefined) {
      filters[field] = Number(query[field]);
    }
  });

  // Handle boolean fields
  booleanFields.forEach((field) => {
    if (query[field] !== undefined) {
      filters[field] = query[field] === "1" || query[field] === "true";
    }
  });

  return filters;
};
